import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  LinearProgress,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import { listCourses } from "../../api/assessmentHierarchy";
import {
  TEST_STATUSES,
  TEST_DIFFICULTIES,
  DIFFICULTY_COLORS,
  DISTRIBUTION_LEVELS,
  listTests,
  listTestTypes,
  deleteTest,
  publishTest,
  duplicateTest,
  previewTest,
  apiErrorMessage,
} from "../../api/assessmentTests";
import useDebouncedValue from "./useDebouncedValue";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
const ACCENT = "#17479e";

const allColumns = [
  { key: "id", label: "ID", cellCss: { minWidth: "60px", width: "60px" }, getValue: (r) => r.id },
  { key: "name", label: "Name", cellCss: { align: "left", textAlign: "left", minWidth: "240px" }, getValue: (r) => r.name },
  { key: "course", label: "Exam", cellCss: { minWidth: "160px" }, getValue: (r) => r.courseName },
  { key: "type", label: "Type", cellCss: { minWidth: "120px" }, getValue: (r) => r.testTypeName },
  { key: "duration", label: "Duration (min)", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.durationMinutes },
  { key: "questions", label: "Questions", cellCss: { align: "center", minWidth: "100px" }, getValue: (r) => r.totalQuestions },
  { key: "marksPerQuestion", label: "Marks/Q", cellCss: { align: "center", minWidth: "90px" }, getValue: (r) => r.marksPerQuestion },
  { key: "negativeMarks", label: "Neg. Marks/Q", cellCss: { align: "center", minWidth: "110px" }, getValue: (r) => r.negativeMarksPerQuestion },
  { key: "premium", label: "Premium", cellCss: { align: "center", minWidth: "100px" }, getValue: (r) => r.requiresPremiumPlan },
  { key: "status", label: "Status", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.status },
  { key: "displayOrder", label: "Display Order", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.displayOrder },
  { key: "description", label: "Description", cellCss: { minWidth: "240px" }, getValue: (r) => r.description },
  { key: "difficulty", label: "Difficulty", cellCss: { minWidth: "120px" }, getValue: (r) => r.difficulty },
];

// Columns that start hidden. The picker persists which columns the user HID, so
// columns added later show up by default, and these start hidden until toggled.
const DEFAULT_HIDDEN_KEYS = ["description", "difficulty"];
const STORAGE_KEY = "assessmentTestsHiddenColumns";

const statusCfg = {
  DRAFT: { label: "Draft", color: "#b26a00", bg: "#fff3e0" },
  PUBLISHED: { label: "Published", color: "#2e7d32", bg: "#e8f5e9" },
  ARCHIVED: { label: "Archived", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

function PremiumChip({ value }) {
  return (
    <Chip
      label={value ? "Premium" : "Free"}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.7rem",
        color: value ? "#6a1b9a" : "#555",
        backgroundColor: value ? "#f3e5f5" : "#f0f0f0",
      }}
    />
  );
}

const difficultyLabel = (value) => TEST_DIFFICULTIES.find((d) => d.value === value)?.label ?? value ?? "—";

const loadHiddenColumns = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const hidden = JSON.parse(raw);
      if (Array.isArray(hidden)) return new Set(hidden);
    }
  } catch {}
  return new Set(DEFAULT_HIDDEN_KEYS);
};

const formatMarks = (value) => {
  if (value == null) return "—";
  const n = Number(value);
  return Number.isNaN(n) ? "—" : Number(n.toFixed(2));
};

// colorOf (optional) tints each row's bar and count; without it the bars use the theme colour.
function DistributionTable({ title, rows, labelOf, colorOf }) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mb: 1 }}>{title}</Typography>
      {rows?.length ? (
        <Stack spacing={1}>
          {rows.map((row) => (
            <Box key={labelOf(row)}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: colorOf ? 600 : 400, color: colorOf?.(row) }}>
                  {labelOf(row)}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                  {row.questionCount} ({Number(row.percentage ?? 0).toFixed(1)}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Number(row.percentage ?? 0))}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  ...(colorOf && {
                    backgroundColor: `${colorOf(row)}26`,
                    "& .MuiLinearProgress-bar": { backgroundColor: colorOf(row) },
                  }),
                }}
              />
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No data yet — build this test's distribution.
        </Typography>
      )}
    </Box>
  );
}

function TestPreviewDialog({ open, test, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !test) return;
    setPreview(null);
    setLoading(true);
    previewTest(test.id)
      .then(setPreview)
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to load preview"));
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, test]);

  const levelLabel = DISTRIBUTION_LEVELS.find((l) => l.value === preview?.distributionLevel)?.label;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 2.5,
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>Test Preview</Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>{test?.name}</Typography>
          </Box>
        </Stack>
      </Box>
      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        {loading || !preview ? (
          <LinearProgress />
        ) : (
          <Stack spacing={3}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.5 }}>
              {[
                ["Questions", preview.totalQuestions],
                ["Duration (min)", preview.durationMinutes ?? "—"],
                ["Total Marks", formatMarks(preview.totalMarks)],
                ["Neg. Marks/Q", formatMarks(preview.negativeMarksPerQuestion)],
              ].map(([label, value]) => (
                <Card key={label} sx={{ boxShadow: 1, borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 }, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.2rem" }}>{value ?? "—"}</Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>{label}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
            <DistributionTable
              title="Difficulty Distribution"
              rows={preview.difficultyDistribution}
              labelOf={(r) => difficultyLabel(r.difficulty)}
              colorOf={(r) => DIFFICULTY_COLORS[r.difficulty]}
            />
            <DistributionTable
              title={levelLabel ? `${levelLabel} Distribution` : "Distribution"}
              rows={preview.hierarchyDistribution}
              labelOf={(r) => r.name}
            />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AssessmentTests() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  // The backend has no search/sort params, so both act on the current page only.
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [courseFilter, setCourseFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [testTypes, setTestTypes] = useState([]);
  const [orderBy, setOrderBy] = useState("displayOrder");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [publishTarget, setPublishTarget] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [previewTarget, setPreviewTarget] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const hidden = loadHiddenColumns();
    const visible = allColumns.map((c) => c.key).filter((k) => !hidden.has(k));
    return new Set(visible.length > 0 ? visible : allColumns.map((c) => c.key));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  const coursesById = useMemo(() => new Map(courses.map((c) => [String(c.id), c])), [courses]);

  const loadTestTypes = () => {
    listTestTypes()
      .then((data) => setTestTypes(Array.isArray(data) ? data : []))
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load test types")));
  };

  useEffect(() => {
    loadTestTypes();
  }, []);

  const loadTests = () => {
    setLoading(true);
    listTests({
      page,
      size: rowsPerPage,
      courseId: courseFilter || undefined,
      status: statusFilter || undefined,
      testTypeId: typeFilter || undefined,
    })
      .then((data) => {
        setTests(data.content || []);
        setTotalElements(data.totalElements ?? 0);
      })
      .catch((err) => {
        setTests([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load tests"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    listCourses({ includeInactive: true })
      .then(setCourses)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load exams")));
  }, []);

  useEffect(() => {
    loadTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, courseFilter, statusFilter, typeFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(allColumns.map((c) => c.key).filter((k) => !visibleColumns.has(k))),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  const rows = useMemo(
    () =>
      tests.map((t) => ({
        ...t,
        courseName: coursesById.get(String(t.courseId))?.name || "—",
      })),
    [tests, coursesById],
  );

  const filteredRows = useMemo(() => {
    if (!debouncedSearch) return rows;
    const q = debouncedSearch.toLowerCase();
    return rows.filter((t) => t.name?.toLowerCase().includes(q));
  }, [rows, debouncedSearch]);

  const sortKeyOf = {
    course: "courseName",
    type: "testTypeName",
    duration: "durationMinutes",
    questions: "totalQuestions",
    negativeMarks: "negativeMarksPerQuestion",
    premium: "requiresPremiumPlan",
  };

  const sortedData = useMemo(() => {
    const field = sortKeyOf[orderBy] || orderBy;
    return [...filteredRows].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      const an = Number(aValue);
      const bn = Number(bValue);
      return order === "asc" ? an - bn : bn - an;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = () => {
    deleteTest(deleteTarget.id)
      .then(() => {
        toast.success(deleteTarget.status === "DRAFT" ? "Test deleted!" : "Test archived!");
        setDeleteTarget(null);
        loadTests();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to delete test"));
      });
  };

  const handlePublish = () => {
    setPublishing(true);
    publishTest(publishTarget.id)
      .then(() => {
        toast.success("Test published!");
        setPublishTarget(null);
        loadTests();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to publish test"));
      })
      .finally(() => setPublishing(false));
  };

  const handleDuplicate = (test) => {
    duplicateTest(test.id)
      .then(() => {
        toast.success("Test duplicated as a new draft!");
        loadTests();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to duplicate test"));
      });
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: "1 1 600px", alignItems: "center" }}>
              <Tooltip title="Searches the tests on the current page" arrow placement="top">
                <TextField
                  size="small"
                  placeholder="Search names"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 220 }}
                />
              </Tooltip>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Exam</InputLabel>
                <Select
                  label="Exam"
                  value={courseFilter}
                  onChange={(e) => {
                    setCourseFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All exams</MenuItem>
                  {courses.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All types</MenuItem>
                  {testTypes.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {TEST_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {statusCfg[s].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{ sx: { width: 220, maxHeight: 500, overflowY: "auto", borderRadius: 2 } }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={<Checkbox checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />}
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/assessment/tests/new")}
                {...BUTTON_STYLES.primary}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Add Test
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={orderBy === c.key ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === c.key}
                      direction={orderBy === c.key ? order : "asc"}
                      onClick={() => handleSort(c.key)}
                      sx={{
                        color: "#ffffff !important",
                        "&.Mui-active": { color: "#ffffff !important" },
                        "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                      }}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "190px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
              ) : sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No tests found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.cellCss?.align} sx={{ padding: "10px 12px", ...c.cellCss }}>
                        {(() => {
                          if (c.key === "id") {
                            return (
                              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{row.id}</Typography>
                            );
                          }
                          if (c.key === "name") {
                            return <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.name}</Typography>;
                          }
                          if (c.key === "premium") return <PremiumChip value={row.requiresPremiumPlan} />;
                          if (c.key === "status") return <StatusChip value={row.status} />;
                          if (c.key === "difficulty") return difficultyLabel(row.difficulty);
                          if (c.key === "marksPerQuestion" || c.key === "negativeMarks") {
                            return formatMarks(c.getValue(row));
                          }
                          if (c.key === "description") {
                            const value = c.getValue(row);
                            return (
                              <Tooltip title={value || ""} arrow disableHoverListener={!value}>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    color: "text.secondary",
                                    maxWidth: 240,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {value || "—"}
                                </Typography>
                              </Tooltip>
                            );
                          }
                          return c.getValue(row) ?? "—";
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ padding: "10px 12px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="flex-start">
                        <Tooltip title="Edit Test" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/assessment/tests/${row.id}/edit`)}
                            sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Preview" arrow placement="top">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setPreviewTarget(row)}
                            sx={{ "&:hover": { backgroundColor: "info.light", color: "white" } }}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate Test" arrow placement="top">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleDuplicate(row)}
                            sx={{ "&:hover": { backgroundColor: "secondary.light", color: "white" } }}
                          >
                            <ContentCopyOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={row.status === "DRAFT" ? "Delete Test" : "Archive Test"} arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteTarget(row)}
                            sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {row.status === "DRAFT" && (
                          <Tooltip title="Publish Test" arrow placement="top">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => setPublishTarget(row)}
                              sx={{ "&:hover": { backgroundColor: "success.light", color: "white" } }}
                            >
                              <PublishOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={deleteTarget?.status === "DRAFT" ? "Confirm Delete" : "Confirm Archive"}
        message={
          deleteTarget?.status === "DRAFT"
            ? "This draft test and its distribution will be permanently deleted. This action cannot be undone."
            : "This test has been published, so it will be archived instead of deleted. Archived tests are hidden from students but keep their attempts and results."
        }
        confirmText="DELETE"
        confirmButtonText={deleteTarget?.status === "DRAFT" ? "Delete" : "Archive"}
        cancelButtonText="Cancel"
      />

      <Dialog open={Boolean(publishTarget)} onClose={() => !publishing && setPublishTarget(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", mb: 1 }}>Publish test?</Typography>
          <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
            Publishing &ldquo;{publishTarget?.name}&rdquo; picks the questions for every item in its distribution and locks
            them in. After that, its distribution can no longer be changed. Publishing fails if there aren&apos;t enough
            matching questions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPublishTarget(null)} disabled={publishing}>
            Cancel
          </Button>
          <LoadingButton variant="contained" color="success" loading={publishing} onClick={handlePublish}>
            Publish
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <TestPreviewDialog open={Boolean(previewTarget)} test={previewTarget} onClose={() => setPreviewTarget(null)} />

    </Box>
  );
}

export default AssessmentTests;
